import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { Divider } from "@msuite/ui/divider";
import { FormSwitch } from "@msuite/ui/form";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { uniqueArrayByKey } from "@msuite/utils/array";
import type { ProtocolFormSchema } from "@msuite/utils/schemas";
import type { Nullable } from "@msuite/utils/types";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { UserCard } from "@/components/cards";
import { ContactMemberCard } from "@/components/cards/contact-member-card";
import { ProtocolFormRecipientAdder } from "./protocol-form-recipient-adder";

export function ProtocolFormRecipients() {
	/** Context */
	const methods = useFormContext<ProtocolFormSchema>();
	const values = useWatch({ control: methods.control });

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const hasRecipients =
		values.callRequestDetails?.callRequestById ||
		values.callRequestDetails?.callRequestForId ||
		(values.recipients?.length || 0) > 0;

	/** Functions */
	function handleOnSelectRecipient(item: { type: "user" | "contactMember"; id: string }) {
		const currentRecipents = methods.getValues("recipients") || [];
		currentRecipents.push(item);
		const uniqueRecipients = uniqueArrayByKey(currentRecipents, "id");
		methods.setValue("recipients", uniqueRecipients);
	}

	function handleOnDeleteRecipient(id: Nullable<string>) {
		const currentRecipients = methods.getValues("recipients") || [];
		methods.setValue(
			"recipients",
			currentRecipients.filter((r) => r.id !== id),
		);
	}

	/** Render */
	return (
		<form
			onSubmit={(e) => {
				e.stopPropagation();
				e.preventDefault();
			}}
		>
			<ContentCard
				title={t("protocols.recipients")}
				icon={icon("Users")}
				contentClassName="gap-2"
				variant="plain"
			>
				{!hasRecipients ? (
					<BlankSlate
						label={t("protocols.no_recipients")}
						Icon={icon("UserPlus")}
					/>
				) : (
					<React.Fragment>
						<div className="h-stack justify-end text-xs pr-4">
							<p>{t("protocols.send_notification")}?</p>
						</div>
						{values.callRequestDetails?.callRequestById && (
							<div className="card h-stack pr-1 justify-between">
								<ContactMemberCard
									contactMemberId={values.callRequestDetails.callRequestById}
									variant="plain"
								/>
								<div>
									<FormSwitch
										control={methods.control}
										name="callRequestDetails.callRequestByIdSendNotification"
									>
										<></>
									</FormSwitch>
								</div>
							</div>
						)}
						{values.callRequestDetails?.callRequestForId && (
							<div className="card h-stack pr-1 justify-between">
								<UserCard
									userId={values.callRequestDetails.callRequestForId}
									variant="plain"
								/>
								<div>
									<FormSwitch
										control={methods.control}
										name="callRequestDetails.callRequestForIdSendNotification"
									>
										<></>
									</FormSwitch>
								</div>
							</div>
						)}
						{values.recipients?.map((recipient) => {
							if (!recipient.type) return null;
							if (!recipient.id) return null;
							let renderer = null;
							if (recipient.type === "user") {
								renderer = (
									<UserCard
										userId={recipient.id}
										variant="plain"
									/>
								);
							} else if (recipient.type === "contactMember") {
								renderer = (
									<ContactMemberCard
										contactMemberId={recipient.id}
										variant="plain"
									/>
								);
							}
							if (!renderer) return null;
							return (
								<div
									className="card h-stack justify-between"
									key={recipient.id}
								>
									{renderer}
									<div className="-my-2 -mr-2">
										<Button
											variant="danger-ghost"
											icon={<Icon name="UserRoundMinus" />}
											size="xxs"
											onClick={() => {
												handleOnDeleteRecipient(recipient.id);
											}}
										/>
									</div>
								</div>
							);
						})}
					</React.Fragment>
				)}
				<Divider variant="content-card" />
				<ProtocolFormRecipientAdder onSelect={handleOnSelectRecipient} />
			</ContentCard>
		</form>
	);
}
